import type {
  CheckVehicleResponse,
  Patrol,
  RecordIncidentRequest,
} from '@local/common-lib';
import createServiceFactory from '@mapbox/mapbox-sdk/services/geocoding.js';
import type { Theme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { captureException, captureMessage } from '@sentry/browser';
import { useFormik } from 'formik';
import type { Position } from 'geojson';
import { useSnackbar } from 'notistack';
import { type FC, useEffect, useState } from 'react';
import { useAsyncGeolocation } from '../../../hooks/use-async-geolocation.js';
import { useAuthenticated } from '../../../utils/auth/auth.js';
import { formikCheck } from '../../../utils/validation.js';
import {
  type AddIncidentForm,
  AddIncidentView,
  addIncidentFormContract,
} from './add-incident.view.js';

interface Props {
  onCancel: () => void;
  onDone: () => void;
  patrol: Patrol.Type;
}

export const AddIncident: FC<Props> = ({ onCancel, onDone, patrol }) => {
  const auth = useAuthenticated();
  const { enqueueSnackbar } = useSnackbar();
  const [positionLoading, setPositionLoading] = useState(false);
  const { getPosition, isGeolocationAvailable, isGeolocationEnabled } =
    useAsyncGeolocation({
      onError: (positionError) => {
        console.error(
          'Error getting geolocation',
          positionError?.code,
          positionError,
        );
      },
      watchLocationPermissionChange: true,
    });
  const smallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const geocoding = (import.meta.env.VITE_APP_MAPBOX_TOKEN as string)
    ? createServiceFactory({
        accessToken: import.meta.env.VITE_APP_MAPBOX_TOKEN as string,
      })
    : undefined;
  const [vehicles, setVehicles] = useState<CheckVehicleResponse[]>([]);

  const recordPatrolForm = useFormik<AddIncidentForm>({
    initialValues: {
      people: 0,
      peopleDetails: '',
      what: undefined,
      whatDetails: '',
      when: new Date(),
    },
    // onReset: () => setShowAdd(false),
    onSubmit: async (values) => {
      const body: RecordIncidentRequest = {
        location: values.position
          ? {
              address: values.address,
              latitude: values.position[1],
              longitude: values.position[0],
              notes: values.locationNotes,
            }
          : undefined,
        people: values.people <= 5 ? values.people : undefined,
        peopleDetails: values.peopleDetails || undefined,
        vehicles: vehicles.map(({ previousMentions, ...record }) => record),
        what: values.what ?? 'Unknown',
        whatDetails: values.whatDetails || undefined,
        when: new Date(),
      };

      try {
        await auth.axios.post(
          `/api/v1/patrol/${encodeURIComponent(patrol._id)}/incident`,
          body,
        );
        enqueueSnackbar('Activity recorded', { variant: 'success' });
        recordPatrolForm.resetForm();
        onDone();
        // refresh();
      } catch (err: unknown) {
        captureException(err);
        if (err instanceof Error) {
          enqueueSnackbar(`Unable to record activity: ${err.message}`, {
            variant: 'error',
          });
        } else {
          throw err;
        }
        // refresh();
        // TODO: onCancel()??
      }
    },
    validate: async (value) => formikCheck(addIncidentFormContract, value),
    validateOnBlur: true,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: working, don't mess with it
  useEffect(() => {
    getCurrentPosition();
  }, []);

  const getCurrentPosition = (): void => {
    if (!isGeolocationEnabled) {
      enqueueSnackbar('Location disabled', { variant: 'warning' });
      return;
    }
    if (!isGeolocationAvailable) {
      enqueueSnackbar('Location unavailable', { variant: 'warning' });
      return;
    }

    setPositionLoading(true);
    getPosition()
      .then(async ({ coords }) => {
        if (!coords || !coords.latitude || !coords.longitude) {
          enqueueSnackbar('Location not present', { variant: 'warning' });
          captureMessage('Unable to get current location: no coordinates', {
            level: 'info',
          });
          return;
        }

        const position: Position = [coords.longitude, coords.latitude];
        await recordPatrolForm.setFieldValue('position', position);
        return;
      })
      .catch((err) => {
        captureException(err);
        if (err instanceof Error) {
          enqueueSnackbar(`Unable to get current location: ${err.message}`, {
            variant: 'error',
          });
        } else {
          throw err;
        }
      })
      .finally(() => {
        setPositionLoading(false);
      })
      .catch((err) => {
        captureException(err);
      });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (
      !recordPatrolForm.values.position ||
      !recordPatrolForm.values.position[0] ||
      !recordPatrolForm.values.position[1] ||
      !geocoding
    ) {
      return;
    }

    geocoding
      .reverseGeocode({
        limit: 1,
        query: recordPatrolForm.values.position as [number, number],
      })
      .send()
      .then(async (result) => {
        if (result.body.features?.length) {
          await recordPatrolForm.setFieldValue(
            'address',
            result.body.features[0].place_name,
          );
        }
        return;
      })
      .catch(async (err) => {
        captureException(err);

        await recordPatrolForm.setFieldValue('address', '');

        if (err instanceof Error) {
          enqueueSnackbar(
            `Unable to lookup address from location: ${err.message}`,
            {
              variant: 'error',
            },
          );
        }
      });
  }, [
    recordPatrolForm.values.position?.[0],
    recordPatrolForm.values.position?.[1],
  ]);

  const checkVehicle = async (rego: string): Promise<void> => {
    const { data } = await auth.axios.post<CheckVehicleResponse>(
      `/api/v1/vehicle/${encodeURIComponent(rego)}/check`,
    );
    if (!data) return;
    setVehicles((prev) => [...prev, data]);
  };

  return (
    <AddIncidentView
      checkVehicle={checkVehicle}
      getCurrentPosition={getCurrentPosition}
      isGeolocationAvailable={isGeolocationAvailable}
      isGeolocationEnabled={isGeolocationEnabled}
      onCancel={onCancel}
      positionLoading={positionLoading}
      recordPatrolForm={recordPatrolForm}
      removeVehicle={(rego) =>
        setVehicles((prev) => prev.filter((v) => v.rego !== rego))
      }
      smallScreen={smallScreen}
      vehicles={vehicles}
    />
  );
};
